import {
  GridColDef,
  GridRowParams,
  GridLogicOperator,
} from '@mui/x-data-grid-pro';
import {
  BigQueryPage,
  FirebaseMessage,
  User,
  UserListEntity,
} from '../../../../types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  startUserSearchTaskForOrgUnits,
  getIndexedUsers,
} from '../../../api/users-api';
import useFirebaseMessageHandler from '../../../shared/firebase/useFirebaseMessageHandler';
import SetOrgsDialog from '../../user-management/user-grid-actions/users/set-orgs-dialog';
import { useNavigate } from 'react-router-dom';
import OrgUnitsLimitedVizToolbar from './org-units-limitedviz-toolbar';
import { useStore } from '../../state/zustand-store';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import UserAvatar from '../../../shared/user-avatar';
import { pagesConfig } from '../../../shared/pages-config';
import useGridColumns from '../../../shared/hooks/use-grid-columns-latest';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { useUserSettings } from '../../../utils/use-user-settings';
import StatusLabel from '../../../shared/status-label';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PersonOff from '@mui/icons-material/PersonOff';

interface OrgUnitsLimitedVizGridProps {
  selectedOrgUnitPath: string;
  primaryDomain: string;
  userRoles: string[];
  changeLogRoles: string[];
  gridKey: string;
}

export default function OrgUnitsLimitedVizGrid({
  selectedOrgUnitPath,
  primaryDomain,
  userRoles,
  changeLogRoles,
  gridKey,
}: OrgUnitsLimitedVizGridProps) {
  const navigate = useNavigate();
  const [openMoveUserDialog, setOpenMoveUserDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [userData, setUserData] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'primaryEmail',
        headerName: 'Email',
        sortable: true,
        flex: 2,
        display: 'flex',
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <UserAvatar
                userEmail={params.row.primaryEmail}
                sx={{ width: 36, height: 36 }}
              />
              <Typography variant="body2">{params.row.primaryEmail}</Typography>
            </Stack>
          );
          return avatar;
        },
      },
      {
        field: 'givenName',
        headerName: 'First Name',
        sortable: true,
        flex: 1,
      },
      {
        field: 'familyName',
        headerName: 'Last Name',
        sortable: true,
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        editable: false,
        sortable: false,
        filterable: false,
        width: 120,
        display: 'flex',
        renderCell: (params) => {
          const status = params.row.archived
            ? 'ARCHIVED'
            : params.row.suspended
            ? 'SUSPENDED'
            : 'ACTIVE';
          return <StatusLabel status={status} />;
        },
      },
      {
        field: 'jobTitle',
        headerName: 'Job Title',
        flex: 1,
        editable: false,
        sortable: false,
        filterable: false,
      },
      {
        field: 'orgUnitName',
        headerName: 'Org Unit Name',
        flex: 1,
        editable: false,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          if (row.orgUnitPath === null) return '';
          if (row.orgUnitPath === '/') return primaryDomain;
          return row.orgUnitPath.split('/').slice(-1)[0];
        },
      },
      {
        field: 'orgUnitPath',
        headerName: 'Org Unit Path',
        flex: 1,
        editable: false,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          if (row.orgUnitPath === null) return '';
          if (row.orgUnitPath === '/') return '/' + primaryDomain;
          return row.orgUnitPath;
        },
      },
    ],
    [primaryDomain]
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'orgUnitsGridStates'
  );

  const columnSorting: Record<string, string> = {
    primaryEmail: 'userEmail',
    givenName: 'firstName',
    familyName: 'lastName',
    asc: 'ASCENDING',
    desc: 'DESCENDING',
  };
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    column: 'userEmail' | 'firstName' | 'lastName' | null;
    sortDirection: 'ASCENDING' | 'DESCENDING' | null;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 25,
    page: 0,
    cursor: null,
    column:
      (columnSorting[initialState?.sorting?.sortModel?.[0]?.field || ''] as
        | 'userEmail'
        | 'firstName'
        | 'lastName') || 'userEmail',
    sortDirection:
      (columnSorting[initialState?.sorting?.sortModel?.[0]?.sort || ''] as
        | 'ASCENDING'
        | 'DESCENDING') || null,
  });

  const { firebaseUser } = useStore();
  const { features } = useUserSettings();
  const useBigQueryForUsers = isFeatureEnabled('indexUsersReact', features);

  const {
    data: bqQueryData,
    isLoading: bqIsLoading,
    isFetching: bqIsFetching,
  } = useQuery({
    queryKey: [
      UserKeys.OrgUnitUsers,
      selectedOrgUnitPath,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.column,
      paginationModel.sortDirection,
    ],
    queryFn: async () => {
      const filters = {
        logicOperator: GridLogicOperator.And,
        quickFilterLogicOperator: GridLogicOperator.And,
        quickFilterValues: [],
        items: [
          {
            id: 1,
            field: 'orgUnitPath',
            operator: 'equals',
            value: selectedOrgUnitPath,
          },
        ],
      };
      const sortItems = [
        {
          field: paginationModel.column || 'userEmail',
          sort:
            paginationModel.sortDirection === 'DESCENDING'
              ? ('desc' as const)
              : ('asc' as const),
        },
      ];
      const page: BigQueryPage<UserListEntity[]> = {
        pageSize: paginationModel.pageSize,
        results: [] as UserListEntity[],
        currentPage: paginationModel.page,
        totalCount: 0,
      };
      const res = await getIndexedUsers(page, filters, sortItems);
      const mappedUsers = res.results.map(
        (u: UserListEntity) =>
          ({
            primaryEmail: u.userEmail,
            name: {
              givenName: u.firstName,
              familyName: u.lastName,
              fullName: `${u.firstName} ${u.lastName}`,
            },
            suspended: u.suspended,
            isArchived: u.archived,
            jobTitle: u.jobTitle,
            orgUnitPath: u.orgUnitPath,
            id: u.userId || '',
            isAdmin: false,
            isDelegatedAdmin: false,
            creationTime: u.accountCreatedTime || 0,
            lastLoginTime: null,
            orgUnitName: u.orgUnitName || u.orgUnitPath?.split('/').pop(),
            aliases: u.aliases,
          }) as unknown as User
      );
      return { mappedUsers, totalCount: res.totalCount };
    },
    staleTime: 0,
    meta: {
      errorMessage:
        'There was an issue loading organizational units. Please refresh and try again.',
    },
    enabled: useBigQueryForUsers,
  });

  const { isLoading: gaeIsLoading, isFetching: gaeIsFetching } = useQuery({
    queryKey: [
      UserKeys.OrgUnitUsers,
      selectedOrgUnitPath,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.column,
      paginationModel.sortDirection,
      firebaseUser.firebaseDbPath,
    ],
    queryFn: () => {
      return startUserSearchTaskForOrgUnits(
        {
          orderBy: paginationModel.column || 'userEmail',
          sortOrder: paginationModel.sortDirection || 'ASCENDING',
          searchModel: {
            orgUnitPath: selectedOrgUnitPath,
          },
          messageType: 'orgUserSearch',
        },
        firebaseUser.firebaseDbPath,
        paginationModel.pageSize,
        paginationModel.cursor || '',
        selectedOrgUnitPath
      );
    },
    staleTime: 0,
    meta: {
      errorMessage:
        'There was an issue loading organizational units. Please refresh and try again.',
    },
    enabled: !useBigQueryForUsers && firebaseUser.firebaseDbPath !== '',
  });

  const isTaskLoading = useBigQueryForUsers
    ? bqIsLoading || bqIsFetching
    : gaeIsLoading || gaeIsFetching;

  useEffect(() => {
    if (useBigQueryForUsers && bqQueryData) {
      setUserData(bqQueryData.mappedUsers as User[]);
    }
  }, [bqQueryData, useBigQueryForUsers]);

  // handle loading
  useEffect(() => {
    if (isTaskLoading && !useBigQueryForUsers) {
      setUserData([]);
      setIsLoading(true);
    } else if (isTaskLoading && useBigQueryForUsers) {
      setIsLoading(true);
    } else if (!isTaskLoading && useBigQueryForUsers) {
      setIsLoading(false);
    }
  }, [isTaskLoading, useBigQueryForUsers]);

  const firebaseCallback = useCallback((message: FirebaseMessage) => {
    if (useBigQueryForUsers) return;
    const jsonString = message.message.split(':::')[1];
    if (jsonString !== 'finished' && jsonString !== 'nextLink') {
      const parsedUser = JSON.parse(jsonString) as User;
      setUserData((prev) => [...prev, parsedUser]);
    } else if (jsonString === 'finished') {
      setIsLoading(false);
      setPaginationModel((prev) => ({
        ...prev,
        cursor: null,
      }));
    } else if (jsonString === 'nextLink') {
      setPaginationModel((prev) => ({
        ...prev,
        cursor: message.message.split(':::')[2],
      }));
      setIsLoading(false);
    }
  }, []);

  useFirebaseMessageHandler(
    ['orgUserSearch'],
    firebaseUser.firebaseDbPath,
    firebaseCallback
  );

  const rowData = userData.map((user) => {
    const primaryOrg =
      user.organizations?.find((o) => o.primary) ?? user.organizations?.[0];
    return {
      id: user.id,
      primaryEmail: user.primaryEmail,
      givenName: user.name?.givenName,
      familyName: user.name?.familyName,
      jobTitle: primaryOrg?.title ?? '',
      orgUnitPath: user.orgUnitPath,
      suspended: user.suspended,
      archived: user.isArchived,
    };
  });

  const toolbarProps = useMemo(
    () => ({
      setOpenMoveUserDialog,
      selectedRows,
      userRoles,
      setUserData,
    }),
    [selectedRows, userRoles]
  );

  return (
    <>
      <SharedDataGridServer
        gridKey={gridKey}
        initialState={
          initialState || {
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: 25,
              },
              rowCount: 0,
            },
          }
        }
        columns={computedColumns}
        rows={rowData}
        rowCount={useBigQueryForUsers ? bqQueryData?.totalCount ?? 0 : -1}
        getRowId={(row) => row.primaryEmail}
        slots={{
          toolbar: useToolbar(OrgUnitsLimitedVizToolbar, toolbarProps),
          ...createGridOverlays(
            'Users',
            <PersonOff />,
            'No users found in this organizational unit.'
          ),
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'linear-progress',
          },
        }}
        checkboxSelection
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        loading={isLoading || isTaskLoading}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (isLoading || isTaskLoading) return;
          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
              column: paginationModel.column,
              sortDirection: paginationModel.sortDirection,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: paginationModel.cursor,
              column: paginationModel.column,
              sortDirection: paginationModel.sortDirection,
            });
          }
        }}
        onSortModelChange={(newModel) => {
          if (isLoading || isTaskLoading) return;

          let column: 'userEmail' | 'firstName' | 'lastName' | null = null;
          if (newModel.length > 0) {
            switch (newModel[0].field) {
              case 'primaryEmail':
                column = 'userEmail';
                break;
              case 'givenName':
                column = 'firstName';
                break;
              case 'familyName':
                column = 'lastName';
                break;
              default:
                break;
            }
          }

          setPaginationModel({
            page: 0,
            pageSize: paginationModel.pageSize,
            cursor: null,
            column: column,
            sortDirection:
              newModel.length > 0 && newModel[0].sort === 'asc'
                ? 'ASCENDING'
                : newModel.length > 0 && newModel[0].sort === 'desc'
                ? 'DESCENDING'
                : null,
          });
        }}
        sx={{
          minHeight: 600,
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onRowClick={(row: GridRowParams<User>) => {
          navigate(`/${pagesConfig.users.link}/${row.row.primaryEmail}`);
        }}
      />
      {openMoveUserDialog && (
        <SetOrgsDialog
          open={openMoveUserDialog}
          handleClose={() => setOpenMoveUserDialog(false)}
          selectedUsers={selectedRows}
          setSelectedUsers={setSelectedRows}
          userRoles={userRoles}
          changeLogRoles={changeLogRoles}
        />
      )}
    </>
  );
}
